/**
 * 2D Political Map Projection Utilities
 *
 * Projects 7-dimensional ideology vectors onto a 2D political compass-style grid.
 *
 * Horizontal Axis (X): Left (-100) to Right (+100)
 * Vertical Axis (Y): Pluralist (-100) to Centralized (+100)
 */

import type { IdeologyVector } from '@/types'

// ============================================================================
// Types
// ============================================================================

export interface Coordinates2D {
  x: number  // -100 to +100 (left to right)
  y: number  // -100 to +100 (pluralist to centralized)
}

export interface GridPosition {
  quadrantX: 0 | 1 | 2  // Left, Center, Right
  quadrantY: 0 | 1 | 2  // Pluralist, Institutional, Centralized
  subX: 0 | 1 | 2       // Position within quadrant
  subY: 0 | 1 | 2       // Position within quadrant
}

export interface QuadrantInfo {
  quadrantName: string
  subLabel: string
  gridPosition: GridPosition
  coordinates: Coordinates2D
}

export interface MapEntity {
  id: string
  name: string
  ideology: IdeologyVector
  color?: string
  type: 'party' | 'candidate'
}

// ============================================================================
// Ideology Vector Indices (for reference)
// ============================================================================
// 0: economic_regulation    (-100 = Free Market, +100 = Heavy Regulation)
// 1: social_values          (-100 = Traditional, +100 = Progressive)
// 2: civil_liberties        (-100 = Restrictive, +100 = Permissive)
// 3: national_identity      (-100 = Nationalist, +100 = Cosmopolitan)
// 4: environment            (-100 = Development, +100 = Green)
// 5: foreign_security       (-100 = Isolationist, +100 = Interventionist)
// 6: government_centralization (-100 = Decentralized, +100 = Centralized)

// ============================================================================
// Core Projection Functions
// ============================================================================

/**
 * Calculates the horizontal (Left/Right) axis from a 7D ideology vector.
 *
 * We INVERT axes where high values = left, so the result has left = negative.
 *
 * Formula:
 *   horizontal = (
 *     (economic_regulation * -0.30) +  // High regulation = left, so invert
 *     (social_values * -0.25) +        // Progressive = left, so invert
 *     (national_identity * -0.20) +    // Cosmopolitan = left, so invert
 *     (environment * -0.15) +          // Green = left, so invert
 *     (foreign_security * 0.10)        // Interventionist = right, keep positive
 *   )
 *
 * Weights sum to 1.0
 * Result: -100 = far left, +100 = far right
 *
 * @param ideology - The 7-dimensional ideology vector
 * @returns A value from -100 (far left) to +100 (far right)
 */
export function calculateHorizontalAxis(ideology: IdeologyVector): number {
  const [
    economicRegulation,   // index 0
    socialValues,         // index 1
    ,                     // index 2 (civil_liberties) - not used in horizontal
    nationalIdentity,     // index 3
    environment,          // index 4
    foreignSecurity,      // index 5
    // govCentralization, // index 6 - not used in horizontal
  ] = ideology

  const horizontal =
    (economicRegulation * -0.30) +  // High regulation = left, so invert
    (socialValues * -0.25) +        // Progressive = left, so invert
    (nationalIdentity * -0.20) +    // Cosmopolitan = left, so invert
    (environment * -0.15) +         // Green = left, so invert
    (foreignSecurity * 0.10)        // Interventionist = right, keep positive

  // Clamp to -100 to +100 range
  return Math.max(-100, Math.min(100, Math.round(horizontal)))
}

/**
 * Calculates the vertical (Pluralist/Centralized) axis from a 7D ideology vector.
 *
 * Formula:
 *   vertical = (
 *     (government_centralization * 0.55) +  // Centralized = top, keep positive
 *     (civil_liberties * -0.25) +           // Permissive = bottom, so invert
 *     (foreign_security * 0.12) +           // Interventionist = top (strong state)
 *     (economic_regulation * 0.08)          // Heavy regulation = top (state control)
 *   )
 *
 * Weights sum to 1.0
 * Result: -100 = pluralist (bottom), +100 = centralized (top)
 *
 * @param ideology - The 7-dimensional ideology vector
 * @returns A value from -100 (pluralist/bottom) to +100 (centralized/top)
 */
export function calculateVerticalAxis(ideology: IdeologyVector): number {
  const [
    economicRegulation,     // index 0
    ,                       // index 1 (social_values) - not used in vertical
    civilLiberties,         // index 2
    ,                       // index 3 (national_identity) - not used in vertical
    ,                       // index 4 (environment) - not used in vertical
    foreignSecurity,        // index 5
    govCentralization,      // index 6
  ] = ideology

  const vertical =
    (govCentralization * 0.55) +   // Centralized = top, keep positive
    (civilLiberties * -0.25) +     // Permissive = bottom, so invert
    (foreignSecurity * 0.12) +     // Interventionist = top (strong state)
    (economicRegulation * 0.08)    // Heavy regulation = top (state control)

  // Clamp to -100 to +100 range
  return Math.max(-100, Math.min(100, Math.round(vertical)))
}

/**
 * Projects a 7D ideology vector onto 2D coordinates.
 *
 * @param ideology - The 7-dimensional ideology vector
 * @returns 2D coordinates with x (horizontal) and y (vertical) values
 */
export function project2D(ideology: IdeologyVector): Coordinates2D {
  return {
    x: calculateHorizontalAxis(ideology),
    y: calculateVerticalAxis(ideology)
  }
}

// ============================================================================
// Grid Position Functions
// ============================================================================

/**
 * Converts a -100 to +100 value into a grid index (0, 1, or 2).
 *
 * @param value - A value from -100 to +100
 * @returns Grid index: 0 (Left/Pluralist), 1 (Center/Institutional), 2 (Right/Centralized)
 */
export function getGridIndex(value: number): 0 | 1 | 2 {
  if (value < -33) return 0
  if (value > 33) return 2
  return 1
}

/**
 * Gets the sub-grid position within a quadrant.
 * Each quadrant is divided into a 3x3 sub-grid.
 *
 * @param value - A value from -100 to +100
 * @returns Sub-grid index: 0, 1, or 2 within the quadrant
 */
export function getSubGridIndex(value: number): 0 | 1 | 2 {
  const gridIndex = getGridIndex(value)

  // Each main grid cell spans ~66.67 units
  // Divide each cell into 3 sub-cells (~22.22 units each)

  let rangeStart: number
  let rangeEnd: number

  if (gridIndex === 0) {
    rangeStart = -100
    rangeEnd = -33
  } else if (gridIndex === 1) {
    rangeStart = -33
    rangeEnd = 33
  } else {
    rangeStart = 33
    rangeEnd = 100
  }

  const rangeSize = rangeEnd - rangeStart
  const positionInRange = value - rangeStart
  const normalized = positionInRange / rangeSize

  if (normalized < 0.333) return 0
  if (normalized > 0.667) return 2
  return 1
}

/**
 * Gets the full grid position (quadrant + sub-position) for 2D coordinates.
 *
 * @param coordinates - The 2D coordinates
 * @returns The grid position including quadrant and sub-grid indices
 */
export function getGridPosition(coordinates: Coordinates2D): GridPosition {
  return {
    quadrantX: getGridIndex(coordinates.x),
    quadrantY: getGridIndex(coordinates.y),
    subX: getSubGridIndex(coordinates.x),
    subY: getSubGridIndex(coordinates.y)
  }
}

// ============================================================================
// Quadrant Info Functions
// ============================================================================

import { POLITICAL_MAP_QUADRANTS } from '@/constants/politicalMap'

/**
 * Gets the quadrant name for a given grid position.
 *
 * @param quadrantX - Horizontal quadrant index (0=Left, 1=Center, 2=Right)
 * @param quadrantY - Vertical quadrant index (0=Pluralist, 1=Institutional, 2=Centralized)
 * @returns The name of the quadrant
 */
export function getQuadrantName(quadrantX: 0 | 1 | 2, quadrantY: 0 | 1 | 2): string {
  return POLITICAL_MAP_QUADRANTS.quadrantNames[quadrantY][quadrantX]
}

/**
 * Gets the sub-label within a quadrant for a given position.
 *
 * @param quadrantName - The name of the quadrant
 * @param subX - Horizontal sub-grid index within the quadrant
 * @param subY - Vertical sub-grid index within the quadrant
 * @returns The sub-label for this position, or the quadrant name if no sub-label exists
 */
export function getSubLabel(
  quadrantName: string,
  subX: 0 | 1 | 2,
  subY: 0 | 1 | 2
): string {
  const subLabels = POLITICAL_MAP_QUADRANTS.subLabels[
    quadrantName as keyof typeof POLITICAL_MAP_QUADRANTS.subLabels
  ]

  if (!subLabels) {
    return quadrantName
  }

  // subLabels[subY][subX] - row first (Y), then column (X)
  return subLabels[subY][subX]
}

/**
 * Gets complete quadrant information for an ideology vector.
 *
 * @param ideology - The 7-dimensional ideology vector
 * @returns Full quadrant info including name, sub-label, positions, and coordinates
 */
export function getQuadrantInfo(ideology: IdeologyVector): QuadrantInfo {
  const coordinates = project2D(ideology)
  const gridPosition = getGridPosition(coordinates)

  const quadrantName = getQuadrantName(gridPosition.quadrantX, gridPosition.quadrantY)
  const subLabel = getSubLabel(quadrantName, gridPosition.subX, gridPosition.subY)

  return {
    quadrantName,
    subLabel,
    gridPosition,
    coordinates
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Converts 2D coordinates to SVG/canvas pixel coordinates.
 *
 * @param coordinates - The 2D coordinates (-100 to +100)
 * @param width - The width of the display area
 * @param height - The height of the display area
 * @param padding - Padding around the edges (default 0)
 * @returns Pixel coordinates {px, py}
 */
export function coordinatesToPixels(
  coordinates: Coordinates2D,
  width: number,
  height: number,
  padding: number = 0
): { px: number; py: number } {
  const availableWidth = width - (2 * padding)
  const availableHeight = height - (2 * padding)

  // Convert from [-100, 100] to [0, 1]
  const normalizedX = (coordinates.x + 100) / 200
  // Invert Y so that higher values (centralized) are at the top
  const normalizedY = 1 - (coordinates.y + 100) / 200

  return {
    px: padding + (normalizedX * availableWidth),
    py: padding + (normalizedY * availableHeight)
  }
}

/**
 * Gets the axis label for a given grid index on the horizontal axis.
 *
 * @param index - Grid index (0, 1, or 2)
 * @returns 'Left', 'Center', or 'Right'
 */
export function getHorizontalAxisLabel(index: 0 | 1 | 2): string {
  const labels = ['Left', 'Center', 'Right'] as const
  return labels[index]
}

/**
 * Gets the axis label for a given grid index on the vertical axis.
 *
 * @param index - Grid index (0, 1, or 2)
 * @returns 'Pluralist', 'Institutional', or 'Centralized'
 */
export function getVerticalAxisLabel(index: 0 | 1 | 2): string {
  const labels = ['Pluralist', 'Institutional', 'Centralized'] as const
  return labels[index]
}

/**
 * Gets a descriptive position string for 2D coordinates.
 * Example: "Center-Left, Moderately Centralized"
 *
 * @param coordinates - The 2D coordinates
 * @returns A human-readable position description
 */
export function getPositionDescription(coordinates: Coordinates2D): string {
  const getHorizontalDescription = (x: number): string => {
    if (x < -66) return 'Far Left'
    if (x < -33) return 'Left'
    if (x < -10) return 'Center-Left'
    if (x <= 10) return 'Centrist'
    if (x <= 33) return 'Center-Right'
    if (x <= 66) return 'Right'
    return 'Far Right'
  }

  const getVerticalDescription = (y: number): string => {
    if (y < -66) return 'Highly Pluralist'
    if (y < -33) return 'Pluralist'
    if (y < -10) return 'Moderately Pluralist'
    if (y <= 10) return 'Balanced'
    if (y <= 33) return 'Moderately Centralized'
    if (y <= 66) return 'Centralized'
    return 'Highly Centralized'
  }

  return `${getHorizontalDescription(coordinates.x)}, ${getVerticalDescription(coordinates.y)}`
}
